/**
 * Tiny synthesized chime via Web Audio API — no asset, no licensing concerns.
 *
 * Browsers block autoplay until the user has interacted with the page, so we:
 *   1. Lazily create the AudioContext on the first call (which itself can be
 *      from a user gesture or queued behind one).
 *   2. Expose `armChime()` to attach a one-shot click/touch listener that
 *      unlocks the context. Components show a "tap to enable alerts" pill
 *      and call this when the user taps it.
 *   3. `playChime()` is a no-op if the context is still suspended — never
 *      throws, never blocks.
 *
 * Honors `prefers-reduced-motion` *and* a saved `as_audio_muted` localStorage
 * flag so a user who already heard the alert can mute repeat plays.
 */

let context: AudioContext | null = null;

function ensureContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (context) return context;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context = new Ctor();
  return context;
}

function isMuted(): boolean {
  try {
    return localStorage.getItem('as_audio_muted') === '1';
  } catch {
    return false;
  }
}

export function setChimeMuted(muted: boolean): void {
  try {
    localStorage.setItem('as_audio_muted', muted ? '1' : '0');
  } catch {
    // ignore
  }
}

export function chimeMuted(): boolean {
  return isMuted();
}

/**
 * Attach a one-shot listener to the document that creates+resumes the
 * AudioContext on the next user gesture. After arming, subsequent calls to
 * `playChime()` will actually produce sound.
 */
export function armChime(): void {
  const handler = () => {
    const ctx = ensureContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  };
  document.addEventListener('click', handler, { once: true, passive: true });
  document.addEventListener('touchstart', handler, { once: true, passive: true });
}

export function playChime(): void {
  if (isMuted()) return;
  const ctx = ensureContext();
  if (!ctx || ctx.state !== 'running') return;
  // Two short tones, rising — clear without being alarming.
  const now = ctx.currentTime;
  scheduleTone(ctx, 880, now, 0.18);
  scheduleTone(ctx, 1175, now + 0.22, 0.22);
}

function scheduleTone(ctx: AudioContext, frequency: number, at: number, duration: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.35, at + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + duration + 0.05);
}
